import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useDebounceValue } from 'usehooks-ts';
import NumberInput from '~/components/Inputs/NumberInput';
import { DoseViewData } from '~/state/atoms/dose/doseAtoms';
import useDose from '~/hooks/patient/dose/useDose';

const DOSE_MIN_VALUE = 1;
const DOSE_MAX_VALUE = 1000;
const DOSE_STEP = 1;

const DoseRescaler = ({ dose }: { dose?: DoseViewData }) => {
    const doseScale = dose?.doseScale ?? 1.0;
    const { setDoseScale } = useDose();
    const [inputValue, setInputValue] = useState<number | null>(
        doseScale * 100
    );
    const [error, setError] = useState<string | null>(null);
    const [debouncedInputValue] = useDebounceValue(inputValue, 100);
    const handleInputChange = (newValue: number | null) => {
        setInputValue(newValue);
    };

    useEffect(() => {
        if (debouncedInputValue && !isNaN(debouncedInputValue)) {
            const validValue = validateValue(debouncedInputValue);
            if (validValue === debouncedInputValue) {
                setError(null);
                setDoseScale(debouncedInputValue / 100);
            } else {
                setError(
                    `Value must be between ${DOSE_MIN_VALUE} and ${DOSE_MAX_VALUE}`
                );
            }
        }
    }, [debouncedInputValue, setDoseScale]);

    const validateValue = (value: number) => {
        return Math.min(Math.max(value, DOSE_MIN_VALUE), DOSE_MAX_VALUE);
    };

    return (
        <Box>
            <NumberInput
                value={inputValue}
                precision={1}
                fullWidth
                label={<FormattedMessage id="doseScaling" />}
                error={error}
                min={DOSE_MIN_VALUE}
                max={DOSE_MAX_VALUE}
                step={DOSE_STEP}
                onChange={handleInputChange}
                disabled={!dose?.generated}
                data-testid="rescale-dose-button"
            />
            <Typography variant="body2" fontSize={11} mt={0.5}>
                <FormattedMessage id="doseScalingInfo" />
            </Typography>
        </Box>
    );
};

export default DoseRescaler;
